import { supabase } from '../supabase';
import type { Role } from '../types/database';
import type { CreateRoleInput, UpdateRoleInput } from '../types/roles.types';

export type { Role };

/**
 * Roles are guarded by RLS (admin-only writes) and database triggers
 * (immutable key/is_system, block deleting system or in-use roles), so the
 * service talks to the table directly and surfaces the trigger/constraint
 * errors as friendly messages.
 */
function getRoleErrorMessage(
  message: string,
  fallback = 'Role request failed.'
): string {
  const cleanMessage = message.replace(/^ERROR:\s*/i, '').trim();
  return cleanMessage || fallback;
}

/**
 * Derive a stable, RLS-safe slug from a display name. The key is immutable once
 * created, so it is generated only on create.
 */
export function slugifyRoleKey(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export async function listRoles(): Promise<Role[]> {
  const { data, error } = await supabase
    .from('roles')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('display_name', { ascending: true });

  if (error) {
    throw new Error(
      `Failed to list roles: ${getRoleErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as Role[];
}

export async function getRole(roleId: string): Promise<Role | null> {
  const { data, error } = await supabase
    .from('roles')
    .select('*')
    .eq('id', roleId)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to load role: ${getRoleErrorMessage(error.message)}`
    );
  }

  return data as Role | null;
}

export async function createRole(input: CreateRoleInput): Promise<Role> {
  const displayName = input.displayName.trim();
  const key = slugifyRoleKey(displayName);

  if (!key) {
    throw new Error('Enter a role name using letters or numbers.');
  }

  // Place new roles at the end of the list.
  const { data: existing, error: existingError } = await supabase
    .from('roles')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1);

  if (existingError) {
    throw new Error(getRoleErrorMessage(existingError.message));
  }

  const nextSortOrder = (existing?.[0]?.sort_order ?? -1) + 1;

  const { data, error } = await supabase
    .from('roles')
    .insert({
      key,
      display_name: displayName,
      description: input.description ?? null,
      chip_color: input.chipColor ?? null,
      sort_order: nextSortOrder,
    })
    .select('*')
    .single();

  if (error) {
    // 23505 = unique_violation on roles.key
    if (error.code === '23505') {
      throw new Error('A role with a similar name already exists.');
    }
    throw new Error(
      getRoleErrorMessage(error.message, 'Role was not created.')
    );
  }

  return data as Role;
}

export async function updateRole(input: UpdateRoleInput): Promise<Role> {
  const displayName = input.displayName.trim();

  if (!displayName) {
    throw new Error('Enter a role name.');
  }

  const patch: Record<string, unknown> = {
    display_name: displayName,
    description: input.description ?? null,
    chip_color: input.chipColor ?? null,
  };

  if (input.sortOrder !== undefined) {
    patch.sort_order = input.sortOrder;
  }

  const { data, error } = await supabase
    .from('roles')
    .update(patch)
    .eq('id', input.roleId)
    .select('*')
    .single();

  if (error) {
    throw new Error(
      getRoleErrorMessage(error.message, 'Role was not updated.')
    );
  }

  return data as Role;
}

export async function deleteRole(roleId: string): Promise<void> {
  const { error } = await supabase.from('roles').delete().eq('id', roleId);

  if (error) {
    throw new Error(
      getRoleErrorMessage(error.message, 'Role was not deleted.')
    );
  }
}
